// src/lib/dueChecklist.ts
// Client-side mirror of backend/utils/assessmentScoring.js's
// `checklistItemStatus`. Deliberately the same three states, the same
// 12-month default interval and the same 30-day "due soon" window.
//
// Why a mirror rather than just reading the server's answer: the Pre-Consult
// screen shows a due-checklist card the moment a template loads, but
// `getAssessmentSummary` is only called once at least one field has an answer
// (and then debounced) — so for the whole first stretch of the form there is
// no server verdict to render. The card still has to say something, and the
// only safe thing for it to say is exactly what the server would say.
//
// Keep this in step with assessmentScoring.js. If the two disagree, the same
// screening reads "Due" in the checklist card and green in the summary strip
// directly beneath it, which is worse than either answer alone.

export type DueStatus = 'green' | 'yellow' | 'red';

/** Matches assessmentScoring.js: no interval declared means yearly. */
export const DEFAULT_INTERVAL_MONTHS = 12;
/** Matches assessmentScoring.js: flag as due-soon this many days before the due date. */
export const DUE_SOON_DAYS = 30;

export function dueChecklistStatus(
  lastDone: string | null | undefined,
  intervalMonths: number | undefined,
  now: number = Date.now()
): DueStatus {
  if (!lastDone) return 'red'; // never screened reads as overdue, not unknown
  const due = new Date(lastDone);
  if (Number.isNaN(due.getTime())) return 'red';
  due.setMonth(due.getMonth() + (intervalMonths || DEFAULT_INTERVAL_MONTHS));
  const dueAt = due.getTime();
  if (now > dueAt) return 'red';
  if (now > dueAt - DUE_SOON_DAYS * 86400000) return 'yellow';
  return 'green';
}

/** Anything not green counts toward the "N screenings due" figure the patient
 * banner and the summary strip both show — a screening coming due inside the
 * month is something the nurse should act on at this visit, not next one. */
export function isDue(status: DueStatus): boolean {
  return status !== 'green';
}

export const DUE_STATUS_BADGE: Record<DueStatus, 'success' | 'warn' | 'danger'> = {
  green: 'success',
  yellow: 'warn',
  red: 'danger',
};
